import { formatPhoneNumber, PhoneNumberE164 } from "@sp-crm/core";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { InlineBanner } from "components/ui/inline-banner";
import { InternalLink } from "components/ui/internal-link";
import { Radio } from "components/ui/radio";
import React, { useCallback, useId, useState } from "react";

export const personalSettingsPath = "/settings/account/personal";

type PhoneNumberTarget = "personal" | "another";

interface PersonalNumberWarningProps {
    problem: string;
    fix: string;
}

export const PersonalNumberWarning: React.FC<PersonalNumberWarningProps> = ({
    problem,
    fix,
}) => (
    <InlineBanner type="warning">
        {problem}{" "}
        <InternalLink to={personalSettingsPath} className="underline">
            {fix}
        </InternalLink>
        .
    </InlineBanner>
);

interface PhoneNumberChoiceProps {
    label: string;
    otherPhoneNumber: PhoneNumberE164 | null;
    personalPhoneNumber: PhoneNumberE164 | null;
    personalNumberWarning: React.ReactElement | null;
    otherNumberWarning: React.ReactElement | null;
    saveFailureMessage: string;
    onUseOtherNumber: (phoneNumber: PhoneNumberE164) => Promise<void>;
    onUsePersonalNumber: () => Promise<void>;
}

const failureMessage = (error: unknown, fallback: string): string =>
    error instanceof Error && error.message ? error.message : fallback;

const personalNumberLabel = (personalPhoneNumber: PhoneNumberE164 | null): string =>
    personalPhoneNumber
        ? `Your personal number, ${formatPhoneNumber(personalPhoneNumber)}`
        : "Your personal number";

export const PhoneNumberChoice: React.FC<PhoneNumberChoiceProps> = ({
    label,
    otherPhoneNumber,
    personalPhoneNumber,
    personalNumberWarning,
    otherNumberWarning,
    saveFailureMessage,
    onUseOtherNumber,
    onUsePersonalNumber,
}) => {
    const radioGroupId = useId();
    const [target, setTarget] = useState<PhoneNumberTarget>(
        otherPhoneNumber ? "another" : "personal",
    );
    const [error, setError] = useState<string | null>(null);
    const [failedSaveCount, setFailedSaveCount] = useState(0);

    const choosePersonalNumber = useCallback(async () => {
        setError(null);
        setTarget("personal");
        try {
            await onUsePersonalNumber();
        } catch (e: unknown) {
            setError(failureMessage(e, saveFailureMessage));
            setTarget("another");
        }
    }, [onUsePersonalNumber, saveFailureMessage]);

    const chooseAnotherNumber = useCallback(() => {
        setError(null);
        setTarget("another");
    }, []);

    const commitOtherNumber = useCallback(
        async (newPhoneNumber: PhoneNumberE164) => {
            setError(null);
            try {
                if (newPhoneNumber) {
                    await onUseOtherNumber(newPhoneNumber);
                } else {
                    await onUsePersonalNumber();
                    setTarget("personal");
                }
            } catch (e: unknown) {
                setError(failureMessage(e, saveFailureMessage));
                setFailedSaveCount(count => count + 1);
            }
        },
        [onUseOtherNumber, onUsePersonalNumber, saveFailureMessage],
    );

    return (
        <div className="space-y-2">
            <div>{label}</div>
            <div className="flex flex-wrap items-center gap-x-3">
                <div className="flex-none">
                    <Radio
                        name={radioGroupId}
                        checked={target === "personal"}
                        onChange={choosePersonalNumber}
                        label={personalNumberLabel(personalPhoneNumber)}
                    />
                </div>
                {personalPhoneNumber ? (
                    <InternalLink to={personalSettingsPath} className="ml-6 sm:ml-0">
                        View Personal Settings
                    </InternalLink>
                ) : null}
            </div>
            {target === "personal" && personalNumberWarning ? (
                <div className="ml-6">{personalNumberWarning}</div>
            ) : null}
            <Radio
                name={radioGroupId}
                checked={target === "another"}
                onChange={chooseAnotherNumber}
                label="Another number"
            />
            {target === "another" ? (
                <div className="ml-6 max-w-md">
                    <PhoneInputE164
                        key={failedSaveCount}
                        initial={otherPhoneNumber}
                        placeholder="Enter a phone number"
                        showButtons={false}
                        onCommit={commitOtherNumber}
                    />
                </div>
            ) : null}
            {target === "another" && otherNumberWarning ? (
                <div className="ml-6">{otherNumberWarning}</div>
            ) : null}
            {error ? <InlineBanner type="error">{error}</InlineBanner> : null}
        </div>
    );
};
